import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import styles from "./Forms.module.css";
import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import FileUpload from "../UILibrary/Upload";
import { defaultValues } from "Utils/Global/globalConstants";
import type {
  EditCategoryFormProps,
  ICreateCategoryProps,
  LoadedCategory,
} from "@model/Category.model";
import type { ExistingUploadFile } from "@model/UIComponents.model";
import { CategoriesService } from "../../../Logic/CategoriesService";
import { useNavigate } from "react-router-dom";
import useImageUploadRules from "../../../Logic/Functions/Hooks/useImageUploadRules";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";

const TITLE_MAX = 50;
const DESC_MAX = 500;

const EditCategoryForm: React.FC<EditCategoryFormProps> = ({ slug }) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<ICreateCategoryProps>({
    mode: "onSubmit",
    defaultValues,
  });

  const categoriesService = useMemo(() => new CategoriesService(), []);
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | undefined>();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCategory, setLoadedCategory] = useState<LoadedCategory | null>(
    null
  );
  const [existingImageFiles, setExistingImageFiles] = useState<
    ExistingUploadFile[]
  >([]);
  const [isExistingImageRemoved, setIsExistingImageRemoved] = useState(false);
  const titleText = useWatch({ control, name: "title" }) ?? "";
  const descriptionText = useWatch({ control, name: "description" }) ?? "";
  const hasSavedImage =
    !isExistingImageRemoved && Boolean(loadedCategory?.imageUrl);
  const authStore = UserAuthenticationStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [didSave, setDidSave] = useState(false);

  const {
    imageRules: uploadRules,
    helperText: uploadHelperText,
    allowedTypes: allowedMimeTypes,
    buildImageSrc: buildExistingImageUrl,
  } = useImageUploadRules({
    mode: "image",
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  useEffect(() => {
    categoriesService.handleGetCategoryBySlug({
      slug,
      page: 1,
      perPage: 1,
      setLoading: setIsLoading,
      setApiError: (msg) => setApiError(msg ?? undefined),
      setCategory: (category) => {
        setCategoryId(category?.id ?? null);
        const state = categoriesService.prepareEditCategoryFormState({
          category,
          defaultValues,
          buildImageUrl: buildExistingImageUrl,
        });

        setLoadedCategory(state.category);
        reset(state.formValues);
        setIsExistingImageRemoved(false);
        setExistingImageFiles(state.existingImages);
      },

      setProducts: () => {},
      setMeta: () => {},
    });
  }, [slug, reset, buildExistingImageUrl]);

  const handleValidSubmit = async (formValues: ICreateCategoryProps) => {
    await categoriesService.handleEditCategory({
      id: categoryId!,
      data: formValues,
      token: authStore.authenticationState?.token ?? "",
      setLoading: setIsLoading,
      setApiError: (msg) => setApiError(msg ?? undefined),
      setSuccessMsg: (msg) => {
        setSuccessMsg(msg);
        if (msg) setDidSave(true);
      },
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate("/", { replace: true });
      },
      isExistingImageRemoved,
    });
  };

  const handleInvalidSubmit = () => {
    categoriesService.handleInvalidEditCategoryForm({
      setApiError,
      setSuccessMsg,
    });
  };
  const handleCancel = () => {
    categoriesService.resetEditCategoryForm({
      didSave,
      loadedCategory,
      defaultValues,
      buildImageUrl: buildExistingImageUrl,
      reset,
      setExistingImageFiles,
      setIsExistingImageRemoved,
      clearMessages: () => {
        setApiError(undefined);
        setSuccessMsg(null);
      },
    });
  };

  return (
    <div className={styles.catPageWrapper} data-testid="ec-page">
      <div className={styles.catContainerNarrow} data-testid="ec-container">
        <div className={styles.catCard} data-testid="ec-card">
          <div className={styles.catCardHeader} data-testid="ec-header">
            <div>
              <h2 className={styles.catCardTitle} data-testid="ec-title">
                Edit Category
              </h2>
              <p className={styles.catCardSub} data-testid="ec-subtitle">
                Update details below
              </p>
            </div>
          </div>

          <form
            className={styles.catCardBody}
            onSubmit={handleSubmit(handleValidSubmit, handleInvalidSubmit)}
            data-testid="ec-form"
          >
            <section
              className={styles.catCardSection}
              data-testid="ec-section-title"
            >
              <div className={styles.catSectionTop}>
                <label className={styles.catLabel} htmlFor="ec-title-input">
                  Title
                </label>

                <span
                  className={styles.catCounter}
                  data-testid="ec-title-counter"
                >
                  {titleText.length}/{TITLE_MAX}
                </span>
              </div>

              <Controller
                name="title"
                control={control}
                rules={{
                  required: "Title is required",
                  validate: (value: string) =>
                    (value?.trim().length ?? 0) <= TITLE_MAX ||
                    `Title must be at most ${TITLE_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <TextInput
                      label=""
                      placeholder="Enter category title"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                      data-testid="ec-title-input"
                    />
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles.catCardSection}
              data-testid="ec-section-description"
            >
              <div className={styles.catSectionTop}>
                <label
                  className={styles.catLabel}
                  htmlFor="ec-description-input"
                >
                  Description
                </label>

                <span
                  className={styles.catCounter}
                  data-testid="ec-desc-counter"
                >
                  {descriptionText.length}/{DESC_MAX}
                </span>
              </div>

              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Description is required",
                  validate: (value: string) =>
                    (value?.trim().length ?? 0) <= DESC_MAX ||
                    `Description must be at most ${DESC_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <textarea
                      id="ec-description-input"
                      className={styles.contactTextarea}
                      placeholder="Write the category description..."
                      rows={6}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-testid="ec-description-input"
                    />

                    {fieldState.error?.message && (
                      <div
                        className={`${styles.formErrorText} ${styles.carMargintop}`}
                        data-testid="ec-description-error"
                      >
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles.catCardSection}
              data-testid="ec-section-image"
            >
              <div className={styles.catSectionTop}>
                <label className={styles.catLabel}>Image</label>
              </div>

              <Controller
                name="image"
                control={control}
                rules={{
                  validate: (fileList: FileList | null | undefined) => {
                    const nextFile = fileList?.[0];
                    if (!nextFile && hasSavedImage) return true;
                    if (!nextFile) return "Image is required";
                    return uploadRules.validate(fileList);
                  },
                }}
                render={({ field, fieldState }) => (
                  <FileUpload
                    value={(field.value ?? null) as unknown as FileList | null}
                    onChange={(next) => {
                      setIsExistingImageRemoved(false);
                      field.onChange(next);
                    }}
                    mimeTypes={allowedMimeTypes}
                    required={!hasSavedImage}
                    maxSizeMB={5}
                    maxFiles={1}
                    existingFiles={existingImageFiles}
                    onRemoveExisting={() => {
                      setExistingImageFiles([]);
                      setIsExistingImageRemoved(true);
                      setValue("image", null as unknown as FileList);
                    }}
                    showInlineError={false}
                    helperText={
                      hasSavedImage
                        ? "Optional. Upload to replace current image."
                        : uploadHelperText
                    }
                    error={fieldState.error?.message || null}
                    data-testid="ec-image"
                    parentTestId="ec-image"
                  />
                )}
              />
            </section>

            {successMsg && (
              <div className={styles.successBanner} data-testid="ec-success">
                {successMsg}
              </div>
            )}

            {apiError && (
              <div className={styles.formErrorText} data-testid="ec-api-error">
                {apiError}
              </div>
            )}

            <div className={styles.catCardFooter} data-testid="ec-footer">
              <div className={styles.catActionsRow} data-testid="ec-actions">
                <button
                  type="button"
                  className={styles.catBtnGhost}
                  onClick={handleCancel}
                  disabled={isSubmitting || isLoading}
                  data-testid="ec-cancel"
                >
                  Cancel
                </button>

                <Submission
                  disabled={isSubmitting || isLoading}
                  buttonText={
                    isSubmitting || isLoading ? "Saving..." : "Save Category"
                  }
  
                  className={styles.catBtn}
                  data-testid="ec-submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCategoryForm;
